/**
 * Attendance summary cards component - Total sessions, duration, and revenue
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, DollarSign } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { AttendanceSummary as SummaryType } from "../types";

interface AttendanceSummaryProps {
  totalSessions: number;
  totalDuration: number;
  totalPrice: number;
  totalProfit?: number;
  title?: string;
}

export function AttendanceSummary({
  totalSessions,
  totalDuration,
  totalPrice,
  totalProfit,
  title = "attendance:summary.title",
}: AttendanceSummaryProps) {
  const { t } = useTranslation();

  const hours = Math.floor(totalDuration / 60);
  const minutes = totalDuration % 60;

  return (
    <div
      className={`grid grid-cols-1 ${totalProfit === undefined ? "md:grid-cols-3" : "md:grid-cols-4"} gap-4 mb-6`}
    >
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {t(title)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{totalSessions}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t("attendance:summary.totalDuration")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">
            {hours}
            {t("attendance:timeUnits.hours")} {minutes}
            {t("attendance:timeUnits.minutes")}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            {t(
              totalProfit === undefined
                ? "attendance:summary.totalRevenue"
                : "attendance:summary.selectedIncome"
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">€{totalPrice.toFixed(2)}</div>
        </CardContent>
      </Card>

      {totalProfit !== undefined && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              {t("attendance:summary.selectedProfit")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">€{totalProfit.toFixed(2)}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
